/**
 * Projekt-Kategorisierung: Kategorie → Unterkategorien.
 *
 * Bis 2026-09 stand der Katalog als Konstante in ProjectMasterData.tsx und
 * war nirgends pflegbar — ein Kreidesilo passte in keine der drei
 * Unterkategorien von „Energie- & Industrieanlagen". Jetzt gilt:
 *
 *  · DEFAULT_PROJECT_CATEGORIES ist der eingebaute Standardkatalog.
 *  · profiles.settings.projectCategories (ProjectCategoryDef[]) ist der
 *    vom Betreiber gepflegte Katalog. Sobald er existiert und nicht leer
 *    ist, ersetzt er den Standard VOLLSTAENDIG — kein tiefes Mischen, damit
 *    eine bewusst gestrichene Unterkategorie nicht wieder auftaucht
 *    (dieselbe Regel wie mergeProfileSettings).
 *  · mergeMissingDefaults holt auf Knopfdruck nach, was ein spaeter
 *    erweiterter Standard mehr hat, ohne eigene Eintraege anzutasten.
 *
 * Alte Projekte behalten ihren Wert, auch wenn er nicht mehr im Katalog
 * steht: withCurrentValue haengt ihn an die Auswahlliste an, statt ihn
 * stillschweigend auf leer zu setzen.
 */

import type { ProjectCategoryDef } from '../types';

export type { ProjectCategoryDef };

export type ProjectCategoryCatalog = Record<string, string[]>;

export const DEFAULT_PROJECT_CATEGORIES: ProjectCategoryDef[] = [
    {
        title: 'Hochbau',
        subs: [
            'Wohnungsbau',
            'Gewerbebau',
            'Wohnungs- & Gewerbebau',
            'Industriebau',
            'Öffentlicher Bau',
            'Bildung & Sport (Schulen, Kitas, Sporthallen)',
            'Gesundheit & Pflege',
            'Hotel & Gastronomie',
            'Logistik- & Hallenbau',
            'Parkhäuser & Tiefgaragen',
            'Landwirtschaftliche Bauten',
        ],
    },
    {
        title: 'Tiefbau & Infrastruktur',
        subs: [
            'Straßen-, Erd- & Kanalbau',
            'Gleis- und Bahnbau',
            'Wasser- & Hafenbau',
            'Leitungs- & Rohrleitungsbau',
            'Breitband- & Kabeltiefbau',
            'Kläranlagen & Wasserwerke',
            'Flughafen & Verkehrsanlagen',
            'Deponie- & Erdbau',
        ],
    },
    {
        title: 'Ingenieurbau & Spezialtiefbau',
        subs: [
            'Brücken- und Tunnelbau',
            'Spezialtiefbau & Baugruben',
            'Tragwerke & Masten',
            'Türme & Schornsteine',
            'Behälter, Becken & Stahlbetonsilos',
        ],
    },
    {
        title: 'Energie- & Industrieanlagen',
        subs: [
            'Großkraftwerke',
            'LNG & Chemie',
            'Netzinfrastruktur & Hochspannung',
            'Umspannwerke & Trafostationen',
            'Silos & Lageranlagen',
            'Produktions- & Fertigungsanlagen',
            'Anlagenbau & Rohrleitungen',
            'Fördertechnik & Verladeanlagen',
            'Windenergie (Onshore/Offshore)',
            'Photovoltaik & Solarparks',
            'Wasserstoff, Biogas & Speicher',
            'Tankstellen & Tanklager',
            'Recycling- & Abfallanlagen',
        ],
    },
    {
        title: 'Bestand, Rückbau & Sanierung',
        subs: [
            'Abbruch & Rückbau',
            'Schadstoffsanierung',
            'Modernisierung & Revitalisierung',
            'Dach- & Fassadensanierung',
            'Brandschutzsanierung',
            'Denkmalpflege',
            'Kampfmittel & Altlasten',
        ],
    },
    {
        title: 'Sonstiges',
        subs: [
            'Sonstiges',
            'Freianlagen & Landschaftsbau',
            'Veranstaltungs- & Messebau',
        ],
    },
];

const clean = (s: unknown): string => (typeof s === 'string' ? s.trim() : '');

/**
 * Bereinigt eine Liste: Leerzeilen weg, Dubletten weg (erster gewinnt),
 * Titel getrimmt. Wird bei jedem Lesen UND vor jedem Schreiben angewandt,
 * damit ein Tippfehler in der Oberflaeche nie zwei gleichnamige Kategorien
 * erzeugt (die Auswahl im Projekt arbeitet mit dem Titel als Schluessel).
 */
export const normalizeCategoryDefs = (list: unknown): ProjectCategoryDef[] => {
    if (!Array.isArray(list)) return [];
    const seen = new Set<string>();
    const out: ProjectCategoryDef[] = [];
    for (const raw of list) {
        const title = clean((raw as any)?.title);
        if (!title || seen.has(title)) continue;
        seen.add(title);
        const subSeen = new Set<string>();
        const subs: string[] = [];
        for (const s of Array.isArray((raw as any)?.subs) ? (raw as any).subs : []) {
            const v = clean(s);
            if (!v || subSeen.has(v)) continue;
            subSeen.add(v);
            subs.push(v);
        }
        out.push({ title, subs });
    }
    return out;
};

type CategorySource = { projectCategories?: unknown } | null | undefined;

/** Ob im Profil ein eigener Katalog steht (und nicht der Standard gilt). */
export const hasCustomCategories = (settings: CategorySource): boolean =>
    normalizeCategoryDefs(settings?.projectCategories).length > 0;

/**
 * Der beim Anmelden geladene Katalog der FIRMA (companyProjectCatalog.ts).
 * Modulweit, damit jede Auswahl ihn sieht, ohne dass App.tsx ihn durch vier
 * Komponentenebenen reichen muss. undefined = nicht geladen oder Firma hat
 * keinen eigenen; dann gilt der Standard.
 */
let companyProjectCategories: ProjectCategoryDef[] | undefined;

export const setCompanyProjectCategories = (list: unknown): void => {
    const n = normalizeCategoryDefs(list);
    companyProjectCategories = n.length > 0 ? n : undefined;
};

export const getCompanyProjectCategories = (): ProjectCategoryDef[] | undefined => companyProjectCategories;

/**
 * Der wirksame Katalog als Liste. Reihenfolge: die uebergebenen Quellen
 * (eigenes Profil, report.settings), dann der Katalog der Firma, dann der
 * Standard. Das eigene Profil zuerst, weil der Admin seine Aenderung sofort
 * sehen muss — der Firmenkatalog wird nur beim Anmelden geladen.
 */
export const resolveProjectCategoryDefs = (...sources: CategorySource[]): ProjectCategoryDef[] => {
    for (const s of sources) {
        const own = normalizeCategoryDefs(s?.projectCategories);
        if (own.length > 0) return own;
    }
    return companyProjectCategories ?? DEFAULT_PROJECT_CATEGORIES;
};

/** Derselbe Katalog als Record — die Form, mit der die Auswahlfelder arbeiten. */
export const resolveProjectCategories = (...sources: CategorySource[]): ProjectCategoryCatalog => {
    const out: ProjectCategoryCatalog = {};
    for (const c of resolveProjectCategoryDefs(...sources)) out[c.title] = [...c.subs];
    return out;
};

/**
 * Ergaenzt im eigenen Katalog, was der Standard mehr hat: fehlende
 * Kategorien hinten anhaengen, fehlende Unterkategorien je vorhandener
 * Kategorie hinten anhaengen. Eigene Eintraege und Reihenfolge bleiben.
 */
export const mergeMissingDefaults = (list: ProjectCategoryDef[]): ProjectCategoryDef[] => {
    const out = normalizeCategoryDefs(list).map(c => ({ title: c.title, subs: [...c.subs] }));
    for (const d of DEFAULT_PROJECT_CATEGORIES) {
        const hit = out.find(c => c.title === d.title);
        if (!hit) { out.push({ title: d.title, subs: [...d.subs] }); continue; }
        for (const s of d.subs) if (!hit.subs.includes(s)) hit.subs.push(s);
    }
    return out;
};

/** Anzahl der Eintraege, die mergeMissingDefaults hinzufuegen wuerde. */
export const countMissingDefaults = (list: ProjectCategoryDef[]): { categories: number; subs: number } => {
    const own = normalizeCategoryDefs(list);
    let categories = 0;
    let subs = 0;
    for (const d of DEFAULT_PROJECT_CATEGORIES) {
        const hit = own.find(c => c.title === d.title);
        if (!hit) { categories += 1; subs += d.subs.length; continue; }
        subs += d.subs.filter(s => !hit.subs.includes(s)).length;
    }
    return { categories, subs };
};

/**
 * Auswahlliste plus aktuellem Wert: ein Projekt, dessen Kategorie aus dem
 * Katalog gestrichen wurde, zeigt weiter seinen Wert, statt still auf
 * „keine" zu springen.
 */
export const withCurrentValue = (options: string[], current: string | null | undefined): string[] => {
    const v = clean(current);
    if (!v || options.includes(v)) return options;
    return [...options, v];
};
